import { useAppScopeStore } from '@frontend/scope';
import { useListProvidersMultiQuery, useListProvidersQuery } from '../queries';
import { ListProvidersIO, ListProvidersMultiIO, PageConfig } from '../types';
import { useGetSchedulerV3FeatureFlagDetails } from './use-get-scheduler-v3-feature-flag-details';

type UseGetProvidersV3DataParams = {
  selectedLocationId: string;
  selectedLocationIds?: string[];
  debouncedSearchValue?: string;
  pageConfig?: PageConfig;
  forCalendarView?: boolean;
  isEnabled?: boolean;
  bypassScheduleV3FlagCheck?: boolean;
};

export const useGetProvidersV3Data = ({
  selectedLocationId,
  selectedLocationIds,
  debouncedSearchValue = '',
  pageConfig,
  forCalendarView = false,
  isEnabled = true,
  bypassScheduleV3FlagCheck = false,
}: UseGetProvidersV3DataParams) => {
  const { isScheduleV3FlagEnabled } = useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const parentLocationId = selectedGroupId;
  const isMultiLocation = !isSingleTypeScope;
  const isMultiLocationApiCall = !!parentLocationId && isMultiLocation;
  const request: ListProvidersIO['input'] = {
    locationId: selectedLocationId,
    showHidden: !forCalendarView,
    avoidV2PassThrough: isScheduleV3FlagEnabled,
    ...(pageConfig?.limit && { limit: pageConfig?.limit }),
    ...(pageConfig?.page && { page: pageConfig?.page }),
    ...(debouncedSearchValue && { name: debouncedSearchValue }),
  };

  const singleLocationGetProvidersQuery = useListProvidersQuery({
    request: request,
    options: {
      retry: 1,
      enabled:
        !isMultiLocationApiCall &&
        isEnabled &&
        (isScheduleV3FlagEnabled || bypassScheduleV3FlagCheck) &&
        !!request.locationId,
    },
    bypassScheduleV3FlagCheck,
  });

  const requestForMulti: ListProvidersMultiIO['input'] = {
    ...(pageConfig?.limit && { limit: pageConfig?.limit }),
    ...(pageConfig?.page && { page: pageConfig?.page }),
    ...(debouncedSearchValue && { name: debouncedSearchValue }),
    locationIds: (selectedLocationIds || (selectedLocationId ? [selectedLocationId] : [])).sort(),
    parentLocationId,
    forCalendarView,
    avoidV2PassThrough: isScheduleV3FlagEnabled,
  };

  const multiLocationGetProvidersQuery = useListProvidersMultiQuery({
    request: requestForMulti,
    options: {
      retry: 1,
      enabled:
        isMultiLocationApiCall &&
        isEnabled &&
        (isScheduleV3FlagEnabled || bypassScheduleV3FlagCheck) &&
        !!requestForMulti.parentLocationId &&
        !!requestForMulti.locationIds.length,
    },
    bypassScheduleV3FlagCheck,
  });

  return isMultiLocationApiCall ? multiLocationGetProvidersQuery : singleLocationGetProvidersQuery;
};
